import { useEffect, useRef, useState, type ComponentProps } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, ExternalLink } from "lucide-react";
import { api, formatDate } from "../api";
import { confirmLeave, setLeaveGuard } from "../leave-guard";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { CreativeHubPage } from "./Modules";
import { PageHeader, StatusBadge } from "../components/Ui";
import Dialog from "../components/Dialog";
import CreativeProduction from "../components/CreativeProduction";
import {
  ActionForm as BaseActionForm,
  DataTable,
  Details,
  NoClient,
  RequestView,
  Tabs,
  label,
  sentence,
  useMutation,
  type RecordData,
} from "../components/Workflow";

// Keep submitted inputs stable until the operation finishes.
function ActionForm(props: ComponentProps<typeof BaseActionForm>) {
  return <fieldset disabled={props.busy} style={{ display: "contents" }}><BaseActionForm {...props} /></fieldset>;
}

const safeLink = (value: unknown) => {
  if (typeof value !== "string") return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
};
// Google Drive uploads pass through the API, which takes 4 MB at most.
const DRIVE_LIMIT = 4 * 1024 * 1024;
const isLegal = (industry: string | null | undefined) => /law|legal|attorney/i.test(industry || "");

// The checker's own content types (utils/grammarCheck.js); each changes what it looks for.
const COPY_TYPES = [
  { value: "general", label: "General copy" },
  { value: "rsa", label: "Google search ad" },
  { value: "gbp_post", label: "GBP post" },
  { value: "meta", label: "Meta ad" },
  { value: "sms", label: "SMS" },
  { value: "review_response", label: "Review response" },
];

// Where a queued item goes, as the queue stores it ("platform:content_type").
const DESTINATIONS = [
  { value: "gbp:gbp_post", label: "Google Business Profile post" },
  { value: "gbp:gbp_photo", label: "Google Business Profile photo" },
  { value: "meta:meta_feed", label: "Meta feed post" },
  { value: "meta:meta_stories", label: "Meta story" },
  { value: "linkedin:linkedin_post", label: "LinkedIn post" },
  { value: "google_ads:rsa", label: "Google search ad" },
];
const destinationName = (platform: unknown, type: unknown) =>
  DESTINATIONS.find((d) => d.value === `${platform}:${type}`)?.label || `${label(String(platform || ""))} ${sentence(String(type || "")).toLowerCase()}`.trim();
// A Content review draft's type → where it is usually posted.
const DRAFT_DESTINATION: Record<string, string> = { rsa: "google_ads:rsa", gbp_post: "gbp:gbp_post", meta_feed: "meta:meta_feed" };

type Draft = { id: number; title: string; content_type: string; status: string; fields: Record<string, unknown>; updated_at: string };
type CopyIssue = { type?: string; severity?: string; original?: string; suggestion?: string; explanation?: string };
type CopyCheck = {
  passed: boolean | null;
  issues?: CopyIssue[];
  corrected_text?: string;
  readability_score?: number | null;
  character_count?: number;
  word_count?: number;
  tone_match?: boolean | null;
  error?: string;
};

/** The text to paste for an approved draft, in the order the platform asks for it. */
function draftCopy(draft: Draft): string {
  const fields = draft.fields || {};
  const text = (key: string) => (typeof fields[key] === "string" ? (fields[key] as string).trim() : "");
  const list = (key: string) => (Array.isArray(fields[key]) ? (fields[key] as unknown[]).map((v) => String(v).trim()).filter(Boolean) : []);
  switch (draft.content_type) {
    case "rsa":
      return [
        "Headlines:", ...list("headlines"), "",
        "Descriptions:", ...list("descriptions"),
        ...(text("final_url") ? ["", `Final URL: ${text("final_url")}`] : []),
        ...(text("path_1") || text("path_2") ? [`Display path: ${[text("path_1"), text("path_2")].filter(Boolean).join(" / ")}`] : []),
      ].join("\n");
    case "gbp_post": return text("copy");
    case "meta_feed": return [text("primary_text"), text("headline") && `Headline: ${text("headline")}`, text("description") && `Description: ${text("description")}`].filter(Boolean).join("\n\n");
    case "video_script": return [text("hook"), text("story"), text("offer_cta")].filter(Boolean).join("\n\n");
    case "sms": return text("message");
    case "review_response": return text("response");
    default: return "";
  }
}

async function copyToClipboard(value: string) {
  try { await navigator.clipboard.writeText(value); return true; } catch { return false; }
}

/** A copy check's answer: the verdict, the numbers, each issue in a table, and the corrected text. */
function CopyResult({ result, original }: { result: CopyCheck; original: string }) {
  const [copied, setCopied] = useState(false);
  const issues = result.issues || [];
  const errors = issues.filter((i) => String(i.severity).toLowerCase() === "error").length;
  const corrected = typeof result.corrected_text === "string" && result.corrected_text.trim() && result.corrected_text !== original ? result.corrected_text : "";
  return (
    <div className="copy-result">
      {result.passed === null ? (
        <p className="inline-notice warning" role="status">{result.error || "The check could not be read. Review it by hand."}</p>
      ) : result.passed ? (
        <p className="inline-notice success" role="status">No errors found.{issues.length ? ` ${issues.length} ${issues.length === 1 ? "suggestion" : "suggestions"} below.` : ""}</p>
      ) : (
        <p className="inline-notice danger" role="status">
          {errors ? `${errors} ${errors === 1 ? "error" : "errors"} to fix before this goes out.` : "The check found problems with this copy. Review it before it goes out."}
        </p>
      )}
      {result.passed !== null && (
        <dl className="record-fields copy-stats">
          {result.readability_score != null && <div><dt>Readability</dt><dd>{result.readability_score} out of 10</dd></div>}
          {result.character_count != null && <div><dt>Characters</dt><dd>{result.character_count.toLocaleString()}</dd></div>}
          {result.word_count != null && <div><dt>Words</dt><dd>{result.word_count.toLocaleString()}</dd></div>}
          {result.tone_match != null && <div><dt>Brand tone</dt><dd>{result.tone_match ? "Matches" : "Does not match"}</dd></div>}
        </dl>
      )}
      {issues.length > 0 && (
        <DataTable
          columns={["type", "severity", "original", "suggestion", "why"]}
          rows={issues.map((issue, index) => ({
            id: index,
            type: sentence(String(issue.type || "other")),
            severity: issue.severity || null,
            original: issue.original || null,
            suggestion: issue.suggestion || null,
            why: issue.explanation || null,
          }))}
        />
      )}
      {corrected && (
        <section className="copy-corrected">
          <div className="copy-corrected-head">
            <h3>Corrected text</h3>
            <button type="button" className="button secondary" onClick={async () => setCopied(await copyToClipboard(corrected))}>
              <Copy aria-hidden="true" />{copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="copy-corrected-text">{corrected}</p>
        </section>
      )}
    </div>
  );
}

/** "Queue approved content": pick an approved draft to fill the form, or type the copy. */
function QueueForm({ clientId, busy, onQueue, onCancel }: { clientId: number; busy: boolean; onQueue: (body: RecordData) => Promise<void>; onCancel?: () => void }) {
  const drafts = useApi<Draft[]>(`/api/content-review?client_id=${clientId}`);
  const approved = (drafts.data || []).filter((draft) => draft.status === "approved" && DRAFT_DESTINATION[draft.content_type]);
  const [draftId, setDraftId] = useState("");
  const [queueError, setQueueError] = useState("");
  const [edited, setEdited] = useState(false);
  const draft = approved.find((d) => String(d.id) === draftId);
  return (
    <div onChangeCapture={(event) => { if ((event.target as Element).closest("form")) setEdited(true); }}>
      <div className="operation-form queue-draft-pick">
        <label>
          <span className="field-label">Approved draft</span>
          <select value={draftId} onChange={(event) => {
            if (edited && !window.confirm("Replace your unsaved queue copy with this draft?")) return;
            setDraftId(event.target.value); setEdited(false); setQueueError("");
          }} disabled={drafts.loading || busy}>
            <option value="">None, I'll type the copy</option>
            {approved.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
          <small className="field-hint">
            {drafts.loading ? "Loading approved drafts…" : drafts.error ? "Approved drafts could not load. You can still type the copy." : approved.length ? "Google, GBP and Meta drafts can be queued here. Unchanged copy marks the draft posted when you record publication. If you edit the copy, asset or destination, it becomes a separate queue item." : "No approved Google, GBP or Meta drafts yet. Approve one in Content review, or type the copy."}
          </small>
        </label>
      </div>
      <ActionForm
        key={draftId || "none"}
        submit="Add to queue"
        busyLabel="Queuing…"
        busy={busy}
        onCancel={onCancel}
        fields={[
          { name: "destination", label: "Where it goes", options: DESTINATIONS, value: (draft && DRAFT_DESTINATION[draft.content_type]) || DESTINATIONS[0].value },
          { name: "copy", type: "textarea", required: true, value: draft ? draftCopy(draft) : "" },
          { name: "asset_url", label: "Asset URL", type: "url", value: draft ? safeLink(draft.fields?.asset_url) || "" : "", placeholder: "https://" },
        ]}
        onSubmit={async ({ destination, copy, asset_url }) => {
          if (String(asset_url || "").trim() && !safeLink(asset_url)) {
            setQueueError("Enter a complete HTTPS asset URL, or leave it blank."); return;
          }
          setQueueError("");
          const [platform, content_type] = String(destination).split(":");
          const unchanged = draft && destination === DRAFT_DESTINATION[draft.content_type]
            && String(copy).trim() === draftCopy(draft).trim()
            && String(asset_url || "").trim() === String(draft.fields?.asset_url || "").trim();
          await onQueue({
            platform,
            content_type,
            client_id: clientId,
            payload: { copy, asset_url: asset_url || null, ...(unchanged ? { draft_id: draft.id, draft_title: draft.title, draft_updated_at: draft.updated_at } : {}) },
          });
        }}
      />
      {queueError && <p className="inline-notice danger" role="alert">{queueError}</p>}
    </div>
  );
}

/** One queued item: what to post, a Copy button, the posting steps, then "Record as published". */
function QueueItem({ item, busy, onPublish, onCancel }: { item: RecordData; busy: boolean; onPublish: (url: string) => Promise<void>; onCancel: () => void }) {
  const [copied, setCopied] = useState(false);
  const [publicationError, setPublicationError] = useState("");
  const payload = (item.payload || {}) as RecordData;
  const copy = typeof payload.copy === "string" ? payload.copy : "";
  const asset = safeLink(payload.asset_url);
  const steps: string[] = Array.isArray(item.posting_instructions) ? item.posting_instructions : [];
  return (
    <div className="queue-item">
      <dl className="record-fields">
        <div><dt>Where</dt><dd>{destinationName(item.platform, item.content_type)}</dd></div>
        <div><dt>Status</dt><dd><StatusBadge tone={item.status === "published" ? "success" : item.status === "pending" ? "warning" : "neutral"}>{sentence(String(item.status || ""))}</StatusBadge></dd></div>
        {payload.draft_title && <div><dt>Draft</dt><dd>{String(payload.draft_title)}</dd></div>}
        {safeLink(item.platform_url) && <div><dt>Published post</dt><dd><a href={safeLink(item.platform_url)} target="_blank" rel="noreferrer">{String(item.platform_url)}</a></dd></div>}
      </dl>
      {copy && (
        <section className="copy-corrected">
          <div className="copy-corrected-head">
            <h3>Copy</h3>
            <button type="button" className="button secondary" onClick={async () => setCopied(await copyToClipboard(copy))}>
              <Copy aria-hidden="true" />{copied ? "Copied" : "Copy text"}
            </button>
          </div>
          <p className="copy-corrected-text">{copy}</p>
        </section>
      )}
      {asset && <a className="button secondary" href={asset} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Open asset</a>}
      {steps.length > 0 && (
        <section className="queue-steps">
          <h3>How to post it</h3>
          <ol>{steps.map((step, index) => <li key={index}>{step.replace(/^\d+\.\s*/, "")}</li>)}</ol>
        </section>
      )}
      {item.status === "pending" && (
        <>
          <ActionForm
            submit="Record as published"
            busy={busy}
            fields={[{ name: "platform_url", label: "Published post URL", type: "url", required: true, placeholder: "https://" }]}
            onSubmit={async (data) => {
              const url = safeLink(data.platform_url);
              if (!url) { setPublicationError("Enter a complete HTTPS URL for the published item."); return; }
              setPublicationError("");
              await onPublish(url);
            }}
          />
          {publicationError && <p className="inline-notice danger" role="alert">{publicationError}</p>}
          <button className="text-button queue-cancel" disabled={busy} onClick={onCancel}>Remove from the queue</button>
        </>
      )}
    </div>
  );
}

/** "data:image/jpeg;base64,…" → "jpg"; a link or a PNG → "png". */
const imageExt = (url: unknown) => ({ jpeg: "jpg", webp: "webp" } as Record<string, string>)[/^data:image\/(\w+);/.exec(String(url))?.[1] || ""] || "png";

// The hub's tabs, named for what they do. The tab lives in the URL (?view=), so a link can open "Make an image".
const CREATIVE_VIEWS = ["assets", "drive", "production", "generate", "copy_check", "publishing"];
const CREATIVE_LABELS: Record<string, string> = { assets: "Brand files", drive: "Google Drive", production: "Plan production", generate: "Make an image", copy_check: "Check copy", publishing: "Posting queue" };

function CreativeTools({ clientId, clientName, industry }: { clientId: number; clientName: string; industry: string | null }) {
  const [params, setParams] = useSearchParams();
  const view = CREATIVE_VIEWS.includes(params.get("view") || "") ? params.get("view")! : "assets";
  const setView = (v: string) => setParams((current) => {
    const next = new URLSearchParams(current);
    if (v === "assets") next.delete("view"); else next.set("view", v);
    return next;
  }, { replace: true });
  const [image, setImage] = useState<RecordData | null>(null),
    [check, setCheck] = useState<{ result: CopyCheck; text: string } | null>(null),
    [dialog, setDialog] = useState(""),
    [item, setItem] = useState<RecordData | null>(null);
  const files = useApi<RecordData[]>(view === "drive" ? `/api/drive/files?client_id=${clientId}` : null),
    queue = useApi<RecordData[]>(view === "publishing" ? `/api/push-queue?client_id=${clientId}` : null);
  const mutation = useMutation();
  const inFlight = useRef(false);
  const context = useRef({ view });
  if (context.current.view !== view) context.current = { view };
  const alive = useRef(true);
  const detailRequest = useRef<AbortController | null>(null);
  const [opening, setOpening] = useState(false), [detailError, setDetailError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [productionBusy, setProductionBusy] = useState(false);
  const [feedbackView, setFeedbackView] = useState(view);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; detailRequest.current?.abort(); };
  }, []);
  useEffect(() => {
    detailRequest.current?.abort(); detailRequest.current = null;
    setOpening(false); setDetailError("");
    setDialog(""); setItem(null); setDirty(false);
    setFeedbackView("");
  }, [view]);
  useEffect(() => {
    if (view === "assets") return;
    const atRisk = dirty || mutation.busy || productionBusy;
    setLeaveGuard(atRisk ? "You have unsaved creative content or an operation in progress. Leave this page?" : null);
    const warn = (event: BeforeUnloadEvent) => { if (atRisk) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => { setLeaveGuard(null); window.removeEventListener("beforeunload", warn); };
  }, [dirty, mutation.busy, productionBusy, view, dialog]);
  async function run<T>(path: string, body?: unknown, method = "POST") {
    if (inFlight.current) return;
    inFlight.current = true;
    const started = context.current;
    setFeedbackView(view);
    try {
      const result = await mutation.run<T>(path, body, method);
      return alive.current && context.current === started ? result : undefined;
    } finally { inFlight.current = false; }
  }
  async function openItem(id: unknown) {
    if (detailRequest.current) return;
    const controller = new AbortController(), started = context.current;
    detailRequest.current = controller; setOpening(true); setDetailError("");
    try {
      const detail = await api<RecordData>(`/api/push-queue/${id}`, { signal: controller.signal });
      if (!controller.signal.aborted && alive.current && context.current === started) { setItem(detail); setDialog("item"); }
    } catch (reason) {
      if (!controller.signal.aborted && alive.current && context.current === started) setDetailError(reason instanceof Error ? reason.message : "The queue item could not be loaded.");
    } finally {
      if (detailRequest.current === controller) { detailRequest.current = null; if (alive.current) setOpening(false); }
    }
  }
  const publishing = useRef(false);
  const [publishingBusy, setPublishingBusy] = useState(false);
  const close = () => { if (!inFlight.current && !publishingBusy && (!dirty || confirmLeave())) { setDialog(""); setDirty(false); mutation.setNotice(""); } };
  async function save(path: string, body: unknown, method = "POST", notice = "Saved.") {
    const response = await run<RecordData>(path, body, method);
    if (response) {
      setDialog("");
      setDirty(false);
      if (view === "drive") files.reload();
      if (view === "publishing") queue.reload();
      mutation.setNotice(notice);
    }
    return response;
  }
  async function publish(url: string) {
    if (!item || publishing.current) return;
    if (!safeLink(url)) { mutation.setNotice("Enter a complete HTTPS URL for the published item."); return; }
    publishing.current = true; setPublishingBusy(true);
    try {
      const done = await run<{ draft_posted?: boolean }>(`/api/push-queue/${item.id}/publish`, { platform_url: url, client_id: clientId }, "PUT");
      if (!done) return;
      const notice = done.draft_posted
        ? "Recorded as published. The draft in Content review is marked posted too."
        : "Recorded as published.";
      setDialog("");
      setDirty(false);
      queue.reload();
      mutation.setNotice(notice);
    } finally { publishing.current = false; setPublishingBusy(false); }
  }
  return (
    <div className="workspace-shell creative-shell" onChangeCapture={(event) => {
      if (view !== "assets" && view !== "production" && (event.target as Element).closest(".operation-form")) {
        const form = (event.target as Element).closest(".operation-form");
        setDirty(view === "generate" || view === "copy_check" ? Boolean(form?.querySelector("textarea")?.value.trim()) : true);
        if (view === "copy_check") setCheck(null);
      }
    }}>
      {/* One header for every tab. "Make an image" is a tab of its own, so the header carries no shortcut to it.
          "Check copy", not "Review copy": Content review is a sibling page in the sidebar.
          These tabs are drawn as light pills (content.css). */}
      <PageHeader
        title="Creative hub"
        description={`Brand files, images, copy checks and the posting queue for ${clientName}.`}
      />
      <Tabs
        tabs={CREATIVE_VIEWS}
        labels={CREATIVE_LABELS}
        value={view}
        onChange={(v) => {
          if (v === view || ((dirty || inFlight.current || productionBusy) && !confirmLeave())) return;
          setView(v);
          setItem(null);
          setDialog(""); setDirty(false);
          mutation.setNotice("");
        }}
      />
      {view === "assets" ? (
        <CreativeHubPage clientId={clientId} />
      ) : (
        <>
          {!dialog && feedbackView === view && mutation.feedback}
          {detailError && <p className="inline-notice danger" role="alert">{detailError}</p>}
          {view === "drive" && (
            <section className="panel workflow-panel">
              <p className="workflow-note">
                Files saved to Google Drive or linked from Canva through NOLA. Not everything in your Google Drive.
              </p>
              <div className="workflow-actions">
                <button className="button" onClick={() => setDialog("upload")}>Upload to Drive</button>
                <button className="button secondary" onClick={() => setDialog("canva")}>Link Canva design</button>
                <button className="button secondary" onClick={files.reload}>Refresh</button>
              </div>
              <RequestView request={files}>
                <DataTable
                  rows={(files.data || []).map((row) => ({ ...row, asset_type: row.asset_type ? sentence(String(row.asset_type)) : null }))}
                  columns={["file_name", "asset_type", "created_at"]}
                  empty="No Drive files or Canva links for this client yet."
                  action={(r) =>
                    safeLink(r.file_url) ? (
                      <a className="button secondary" href={safeLink(r.file_url)} target="_blank" rel="noreferrer">
                        Open file
                      </a>
                    ) : null
                  }
                />
              </RequestView>
            </section>
          )}
          {view === "production" && <CreativeProduction key={clientId} clientId={clientId} onBusyChange={setProductionBusy} onDirtyChange={setDirty} />}
          {view === "generate" && (
            // Provider, size and quality are one-word choices: they share one row under the brief.
            <section className="panel workflow-panel creative-generate">
              <p className="workflow-note">
                Uses the AI image service set up in Settings, which may cost money. Check the image before you save it.
              </p>
              <ActionForm
                submit="Generate image"
                busyLabel="Generating…"
                busy={mutation.busy}
                fields={[
                  { name: "user_request", label: "Creative brief", type: "textarea", required: true },
                  { name: "provider", options: [{ value: "openai", label: "OpenAI" }, { value: "gemini", label: "Gemini" }] },
                  {
                    name: "size",
                    options: [
                      { value: "1024x1024", label: "Square (1024 × 1024)" },
                      { value: "1536x1024", label: "Wide (1536 × 1024)" },
                      { value: "1024x1536", label: "Tall (1024 × 1536)" },
                    ],
                  },
                  { name: "quality", options: [{ value: "standard", label: "Standard" }, { value: "hd", label: "High" }] },
                ]}
                onSubmit={async (data) => {
                  if (inFlight.current) return;
                  setImage(null);
                  const r = await run<RecordData>("/api/imagegen/generate", { ...data, client_id: clientId });
                  if (r) { setImage(r); setDirty(false); }
                }}
              />
              {image && (
                <>
                  <Details record={{ optimized_prompt: image.optimized_prompt, generator: image.generator, note: image.note }} />
                  {safeLink(image.image_url) ? (
                    <a href={safeLink(image.image_url)} target="_blank" rel="noreferrer" className="button secondary">
                      Preview generated image
                    </a>
                  ) : typeof image.image_url === "string" && image.image_url.startsWith("data:image/") ? (
                    <img className="generated-preview" src={image.image_url} alt="Generated creative for review" />
                  ) : null}
                  <button
                    className="button"
                    disabled={mutation.busy}
                    onClick={() =>
                      save("/api/drive/save-generated", {
                        client_id: clientId,
                        image_url: image.image_url,
                        file_name: `creative-${Date.now()}.${imageExt(image.image_url)}`,
                        prompt_used: image.optimized_prompt,
                        generator: image.generator,
                      }, "POST", "Saved to Google Drive.")
                    }
                  >
                    Save reviewed image to Drive
                  </button>
                </>
              )}
            </section>
          )}
          {view === "copy_check" && (
            <section className="panel workflow-panel">
              <p className="workflow-note">
                Checks spelling, clarity and tone with the AI service set up in Settings.{isLegal(industry) ? " This client is a law firm, so the check also flags anything bar rules forbid." : ""}
              </p>
              <ActionForm
                submit="Check copy"
                busyLabel="Checking…"
                busy={mutation.busy}
                fields={[
                  { name: "text", label: "Copy to check", type: "textarea", required: true },
                  { name: "content_type", label: "Content type", options: COPY_TYPES, value: "general" },
                ]}
                onSubmit={async (data) => {
                  if (inFlight.current) return;
                  setCheck(null);
                  const text = String(data.text || "");
                  const r = await run<CopyCheck>("/api/grammar/check", { ...data, client_name: clientName, is_legal_client: isLegal(industry) });
                  if (r) { setCheck({ result: r, text }); setDirty(false); }
                }}
              />
              {check && <CopyResult result={check.result} original={check.text} />}
            </section>
          )}
          {view === "publishing" && (
            <section className="panel workflow-panel">
              <p className="workflow-note">
                Queue approved content, post it on the platform by hand, then record the published link here.
              </p>
              <div className="workflow-actions">
                <button className="button" disabled={opening || mutation.busy} onClick={() => setDialog("queue")}>Queue approved content</button>
                <button className="button secondary" onClick={queue.reload}>Refresh</button>
              </div>
              <RequestView request={queue}>
                <DataTable
                  rows={(queue.data || []).map((row) => ({
                    id: row.id,
                    where: <>{destinationName(row.platform, row.content_type)}</>,
                    draft: (row.payload as RecordData | undefined)?.draft_title || null,
                    status: row.status,
                    queued: <>{formatDate(row.created_at)}</>,
                  }))}
                  columns={["where", "draft", "status", "queued"]}
                  empty="Nothing queued. Queue approved content to get posting steps for it."
                  action={(r) => (
                    <button
                      className="button secondary"
                      disabled={opening || mutation.busy}
                      onClick={() => void openItem(r.id)}
                    >
                      Open
                    </button>
                  )}
                />
              </RequestView>
            </section>
          )}
        </>
      )}
      {dialog && (
        <Dialog
          title={
            dialog === "upload"
              ? "Upload to Google Drive"
              : dialog === "canva"
                ? "Link Canva design"
                : dialog === "queue"
                  ? "Queue approved content"
                  : "Posting steps"
          }
          onClose={close}
        >
          {dialog === "upload" ? (
            <form
              className="operation-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const body = new FormData(e.currentTarget);
                const file = body.get("file") as File;
                if (file.size > DRIVE_LIMIT) {
                  mutation.setNotice("Choose a file of 4 MB or less. Store larger files privately in NOLA under Brand files.");
                  return;
                }
                body.set("client_id", String(clientId));
                await save("/api/drive/upload", body, "POST", "Uploaded to Google Drive.");
              }}
            >
              <label>
                <span className="field-label">File<span className="field-required" aria-hidden="true"> *</span></span>
                <input type="file" name="file" disabled={mutation.busy} required />
                <small className="field-hint">Up to 4 MB. Larger files can be stored privately in NOLA under Brand files.</small>
              </label>
              <label>
                <span className="field-label">Description</span>
                <input name="description" disabled={mutation.busy} />
              </label>
              <div className="operation-actions">
                <button type="button" className="button secondary" disabled={mutation.busy} onClick={close}>Cancel</button>
                <button className="button" disabled={mutation.busy}>{mutation.busy ? "Uploading…" : "Upload to Drive"}</button>
              </div>
            </form>
          ) : dialog === "canva" ? (
            <ActionForm
              submit="Link design"
              busyLabel="Linking…"
              busy={mutation.busy}
              onCancel={close}
              fields={[
                { name: "asset_name", label: "Name", required: true },
                { name: "canva_design_url", label: "Canva design URL", type: "url", required: true, placeholder: "https://www.canva.com/design/…" },
              ]}
              onSubmit={async (data) => {
                await save("/api/drive/canva-link", { ...data, client_id: clientId }, "POST", "Canva design linked.");
              }}
            />
          ) : dialog === "queue" ? (
            <QueueForm
              clientId={clientId}
              busy={mutation.busy}
              onCancel={close}
              onQueue={async (body) => { await save("/api/push-queue", body, "POST", "Added to the queue. Open it for the posting steps."); }}
            />
          ) : (
            item && (
              <QueueItem
                key={String(item.id)}
                item={item}
                busy={mutation.busy || publishingBusy}
                onPublish={publish}
                onCancel={() => void save(`/api/push-queue/${item.id}/cancel`, {}, "PUT", "Removed from the queue.")}
              />
            )
          )}
          {feedbackView === view && mutation.feedback}
        </Dialog>
      )}
    </div>
  );
}

export default function CreativeWorkspace() {
  const { clientId, client } = useClientSelection();
  return clientId ? (
    <CreativeTools key={clientId} clientId={clientId} clientName={client?.name || "the active client"} industry={client?.industry ?? null} />
  ) : (
    <NoClient />
  );
}
